import type { AssignmentGradeFormSchema } from '@repo/schemas/assignment/assignmentGrade';
import type { AssignmentDetail, AssignmentResponseWithDetails } from '@repo/types/assignments';
import type { GradePublic } from '@repo/types/grades';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCreateGrade } from '@repo/api/grade/useCreateGrade';
import { useGetGrade } from '@repo/api/grade/useGetGrade';
import { cn } from '@repo/lib/cn';
import { assignmentGradeFormSchema } from '@repo/schemas/assignment/assignmentGrade';
import { AwardIcon, PencilLineIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { GradeMappingPreview } from '@/components/features/grade-scale/GradeMappingPreview';
import { resolveGradePercent } from '@/components/features/grade-scale/resolveGrade';
import { gradePercentage, resolveGradeBand } from '@/components/features/grades/gradeStyle';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { NumberInputHorizontal } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface AssignmentGradeFormProps {
  assignmentId: number;
  assignment: AssignmentDetail;
  submission: AssignmentResponseWithDetails;
}

/**
 * Manual grading for a regular (non-quiz) submission. Shows the current grade
 * inline with an action that opens the grading dialog; the teacher only enters
 * a percentage — the grade stores it raw, and its band label/color are
 * resolved against the assignment's current scale wherever it is shown.
 */
export function AssignmentGradeForm({ assignmentId, assignment, submission }: AssignmentGradeFormProps) {
  const [open, setOpen] = useState(false);
  const gradeQuery = useGetGrade(submission.gradeId ?? undefined);

  const isGraded = submission.gradeId != null;
  const isLoadingGrade = isGraded && gradeQuery.isPending;
  const grade = gradeQuery.data;
  const resolved = grade ? resolveGradeBand(grade) : null;

  return (
    <>
      <div className="border-border-primary bg-card flex w-full items-center gap-3 rounded-xl border p-2.5 sm:w-auto sm:min-w-[220px]">
        <FeaturedIcon size="md" className="shrink-0">
          <AwardIcon />
        </FeaturedIcon>

        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-muted-foreground text-xs font-medium">Ocena</span>
          {isLoadingGrade ? (
            <Skeleton className="mt-1 h-5 w-16" />
          ) : isGraded && resolved ? (
            <span className={cn('truncate text-base leading-tight font-semibold', !resolved.color && 'text-foreground')} style={resolved.color ? { color: resolved.color } : undefined}>
              {resolved.display}
            </span>
          ) : (
            <span className="text-muted-foreground text-base leading-tight font-semibold">Nieoceniono</span>
          )}
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="ml-auto shrink-0 sm:ml-1" onClick={() => setOpen(true)} disabled={isLoadingGrade} aria-label={isGraded ? 'Edytuj ocenę' : 'Oceń'}>
              <PencilLineIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{isGraded ? 'Edytuj ocenę' : 'Oceń'}</TooltipContent>
        </Tooltip>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          {open && <GradeFormFields assignmentId={assignmentId} assignment={assignment} submission={submission} grade={grade} onClose={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

interface GradeFormFieldsProps extends AssignmentGradeFormProps {
  grade: GradePublic | undefined;
  onClose: () => void;
}

function initialPercentage(grade: GradePublic | undefined, assignment: AssignmentDetail): number {
  if (!grade) return 0;
  const percentage = gradePercentage(grade);
  if (percentage !== null) return percentage;
  // a stored band label maps back to its threshold
  return assignment.gradeMapping?.thresholds[grade.value] ?? 0;
}

// Grade.name is validated [2,25] server-side; assignment names run to 100.
function gradeNameFromAssignment(name: string): string {
  const clamped = name
    .trim()
    .slice(0, 25)
    .replace(/[\uD800-\uDBFF]$/, '')
    .trimEnd();
  return clamped.length >= 2 ? clamped : 'Ocena';
}

function GradeFormFields({ assignmentId, assignment, submission, grade, onClose }: GradeFormFieldsProps) {
  const { mutateAsync, isPending, errorMessage } = useCreateGrade();

  const form = useForm<AssignmentGradeFormSchema>({
    resolver: standardSchemaResolver(assignmentGradeFormSchema),
    defaultValues: {
      percentage: initialPercentage(grade, assignment),
      desc: grade?.desc ?? '',
    },
  });

  const percentage: number = useWatch({ control: form.control, name: 'percentage' }) ?? 0;
  const preview = resolveGradePercent(assignment.gradeMapping, percentage);
  const previewValue: string = preview.label ?? `${percentage}%`;

  const onSubmit = (data: AssignmentGradeFormSchema) =>
    mutateAsync({
      assignmentId,
      respId: submission.id,
      name: gradeNameFromAssignment(assignment.name),
      desc: data.desc?.trim() ? data.desc.trim() : undefined,
      value: String(data.percentage),
    })
      .then(() => {
        toast.success(submission.gradeId ? 'Zaktualizowano ocenę' : 'Wystawiono ocenę');
        onClose();
      })
      .catch(() => toast.error(errorMessage.title, { description: errorMessage.detail }));

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
        <DialogBody>
          <DialogHeader className="border-border-primary border-b">
            <FeaturedIcon size="lg">
              <AwardIcon />
            </FeaturedIcon>
            <div className="flex min-w-0 flex-col gap-0.5">
              <DialogTitle>Ocenianie</DialogTitle>
              <DialogDescription>{submission.gradeId ? 'Zapisanie nadpisze obecną ocenę.' : 'Wystaw ocenę za to podejście.'}</DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex grow flex-col gap-4 overflow-y-auto p-4">
            <FormField
              control={form.control}
              name="percentage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Wynik procentowy</FormLabel>
                  <FormControl>
                    <NumberInputHorizontal min={0} max={100} value={field.value} onChange={(value) => field.onChange(value ?? 0)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-2">
              <p className="text-muted-foreground text-sm font-medium">Skala ocen</p>
              <GradeMappingPreview gradeMapping={assignment.gradeMapping} markPercent={percentage} onMarkChange={(value) => form.setValue('percentage', value)} />
              <p className="text-sm">
                Wynikowa ocena:{' '}
                <span className="font-semibold" style={preview.color ? { color: preview.color } : undefined}>
                  {previewValue}
                </span>
              </p>
            </div>

            <FormField
              control={form.control}
              name="desc"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Komentarz (opcjonalnie)</FormLabel>
                  <FormControl>
                    <Textarea {...field} value={field.value ?? ''} placeholder="Komentarz do oceny" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </DialogBody>

        <DialogFooter className="border-border-primary bg-accent dark:bg-card border-t">
          <Button type="button" variant="tertiary" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" variant="secondary" disabled={isPending}>
            {submission.gradeId ? 'Zaktualizuj ocenę' : 'Wystaw ocenę'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
